import { useEffect, useState } from 'react';

import { parseEntityRef } from '@backstage/catalog-model';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';

import {
  MAX_REPOSITORY_NAME_LENGTH,
  getDisplayValueWithStatus,
  getHelperText,
  validateRepositoryName,
} from './DynamicRepositoryNameField.utils';

// Repository type configuration
export const REPOSITORY_TYPES = [
  { value: 'cci', label: 'CircleCI Pipeline' },
  { value: 'container', label: 'Container Image' },
  { value: 'doc', label: 'Documentation' },
  { value: 'e2e', label: 'End-to-End Testing' },
  { value: 'edge', label: 'Edge Computing' },
  { value: 'fe', label: 'Frontend Application' },
  { value: 'gha', label: 'Github Actions' },
  { value: 'gv', label: 'Gridvision (Carrier Energy)' },
  { value: 'integrationadapter', label: 'Integration Adapter' },
  { value: 'lib', label: 'Library' },
  { value: 'misc', label: 'Miscellaneous' },
  { value: 'orb', label: 'CircleCI Orb' },
  { value: 'pipeline', label: 'CI/CD Pipeline' },
  { value: 'qa', label: 'Quality Assurance' },
  { value: 'service', label: 'Service/API' },
  { value: 'shared', label: 'Shared Resource' },
  { value: 'utility', label: 'Utility Tool' },
];

export const DynamicRepositoryNameField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, formContext, name, uiSchema } = props;

  const theme = useTheme();
  const finalRepositoryName = (formContext?.formData?.[name] || '') as string;
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);

  // Extract product area reference from form context for owner resolution (default 'product_area', overridable via ui:options.customProductArea)
  const productAreaFieldName =
    (uiSchema?.['ui:options']?.customProductArea as string) || 'product_area';
  const productAreaRef = formContext?.formData?.[
    productAreaFieldName
  ] as string;

  // Extract template-specific options from uiSchema
  const allowedTypes =
    (uiSchema?.['ui:options']?.allowedTypes as string[]) ||
    REPOSITORY_TYPES.map(t => t.value);
  const defaultType =
    (uiSchema?.['ui:options']?.defaultType as string) || 'lib';

  // Filter repository types based on template restrictions
  const availableRepositoryTypes = REPOSITORY_TYPES.filter(type =>
    allowedTypes.includes(type.value),
  );

  // Internal state for sub-fields
  const [ownerPrefix, setOwnerPrefix] = useState<string>('');
  const [repositoryType, setRepositoryType] = useState<string>(() => {
    // Ensure the default type is actually allowed for this template
    if (allowedTypes.includes(defaultType)) {
      return defaultType;
    }
    // Fall back to the first available type if default is not allowed
    return availableRepositoryTypes.length > 0
      ? availableRepositoryTypes[0].value
      : 'lib';
  });
  const [repositoryPurpose, setRepositoryPurpose] = useState<string>('');
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
  }>({ isValid: true, errors: [] });

  // Update owner prefix when product area changes - fetch from Catalog API
  useEffect(() => {
    if (productAreaRef) {
      const fetchOwnerPrefix = async () => {
        try {
          // Parse the entity reference
          const entityRef = parseEntityRef(productAreaRef);

          // Fetch the group entity from the catalog
          const entity = await catalogApi.getEntityByRef(entityRef);

          if (entity) {
            // Extract the owner prefix from the annotation
            const annotatedOwnerPrefix =
              entity.metadata.annotations?.['carrier.com/owner-prefix'];

            if (annotatedOwnerPrefix) {
              setOwnerPrefix(annotatedOwnerPrefix);
            } else {
              // Group does not have carrier.com/owner-prefix annotation
              errorApi.post(
                new Error(
                  `Group ${productAreaRef} does not have carrier.com/owner-prefix annotation`,
                ),
              );
              setOwnerPrefix('unknown');
            }
          } else {
            // Could not find group entity
            errorApi.post(
              new Error(`Could not find group entity: ${productAreaRef}`),
            );
            setOwnerPrefix('unknown');
          }
        } catch (error) {
          // Error fetching owner prefix from group entity
          errorApi.post(
            new Error(
              `Error fetching owner prefix from group entity: ${error}`,
            ),
          );
          setOwnerPrefix('unknown');
        }
      };

      fetchOwnerPrefix();
    } else {
      setOwnerPrefix('');
    }
  }, [productAreaRef, catalogApi, errorApi]);

  // Auto-construct repository name progressively as fields are filled
  useEffect(() => {
    if (ownerPrefix) {
      let partialName = ownerPrefix;

      if (repositoryType) {
        partialName += `-${repositoryType}`;

        if (repositoryPurpose) {
          partialName += `-${repositoryPurpose}`;
        }
      }

      onChange(partialName);
    } else {
      // Clear the field if no owner prefix is available
      onChange('');
    }
  }, [ownerPrefix, repositoryType, repositoryPurpose, onChange]);

  // Validate the current value
  useEffect(() => {
    if (finalRepositoryName) {
      const result = validateRepositoryName(
        finalRepositoryName,
        ownerPrefix,
        repositoryType,
      );
      setValidationResult(result);
    } else {
      setValidationResult({ isValid: true, errors: [] });
    }
  }, [finalRepositoryName, ownerPrefix, repositoryType]);

  const allErrors = [
    ...new Set([...(rawErrors || []), ...validationResult.errors]),
  ];
  const hasErrors = allErrors.length > 0;
  const displayValidation = {
    isValid: validationResult.isValid && !rawErrors?.length,
    errors: allErrors,
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Repository Configuration
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        See{' '}
        <a
          href="https://carrier-digital.atlassian.net/wiki/spaces/DFE/pages/2658664449/DPX+Repository+Naming+Conventions"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: theme.palette.primary.main,
          }}
        >
          DPX Repository Naming Conventions
        </a>{' '}
        for more details on repository naming conventions. The final name must
        be at most {MAX_REPOSITORY_NAME_LENGTH} characters.
      </Typography>

      <Grid container spacing={3}>
        {/* Repository Type Selection */}
        <Grid item xs={12} md={4}>
          <Box>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="repository-type-label" required>
                Repository Type
              </InputLabel>
              <Select
                labelId="repository-type-label"
                label="Repository Type"
                value={repositoryType}
                onChange={e => setRepositoryType(e.target.value as string)}
                required
                variant="outlined"
              >
                {availableRepositoryTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Typography variant="body2">{type.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select the category that best describes your repository (options
                dependent on the selected template)
              </FormHelperText>
            </FormControl>
          </Box>
        </Grid>

        {/* Repository Purpose Input */}
        <Grid item xs={12} md={4}>
          <Box>
            <FormControl fullWidth margin="normal">
              <TextField
                label="Repository Purpose"
                value={repositoryPurpose}
                onChange={e =>
                  setRepositoryPurpose(e.target.value.toLowerCase())
                }
                placeholder="my-awesome-library"
                helperText="Brief, descriptive name (lowercase letters, numbers, hyphens)"
                required
                variant="outlined"
              />
            </FormControl>
          </Box>
        </Grid>

        {/* Final Repository Name Display */}
        <Grid item xs={12} md={4}>
          <Box>
            <FormControl
              fullWidth
              margin="normal"
              error={hasErrors}
              variant="outlined"
            >
              <TextField
                name={name}
                label="Final Repository Name"
                value={
                  finalRepositoryName
                    ? getDisplayValueWithStatus(
                        finalRepositoryName,
                        displayValidation,
                        ownerPrefix,
                        repositoryType,
                        repositoryPurpose,
                      )
                    : ''
                }
                InputProps={{
                  readOnly: true,
                  style: {
                    cursor: 'default',
                    fontWeight: '500',
                  },
                }}
                InputLabelProps={{
                  required: false,
                  style: {
                    fontWeight: '600',
                  },
                }}
                disabled={false}
                required
                error={hasErrors}
                placeholder="Select fields to build repository name..."
                variant="outlined"
              />
              {hasErrors && (
                <FormHelperText error>{allErrors[0]}</FormHelperText>
              )}
              {!hasErrors && (
                <FormHelperText>
                  {getHelperText(
                    finalRepositoryName,
                    displayValidation,
                    ownerPrefix,
                    repositoryType,
                    repositoryPurpose,
                  )}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Grid>
      </Grid>

      {/* Additional Validation Errors Display (if more than one error) */}
      {allErrors.length > 1 && (
        <Box mt={2}>
          {allErrors.slice(1).map((error, index) => {
            return (
              <Typography
                key={error + index}
                variant="caption"
                color="error"
                display="block"
              >
                {error}
              </Typography>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
